/**
 * <TutorOfferFlow> — le geste sur une offre de cours : l'ouvrir, l'accepter, ou retirer sa
 * candidature (template).
 *
 * Legacy `ProposalsTabScreen` → `handleAccept` / `handleWithdraw`, et le « Accepter » des pages d'un
 * lien. Deux `DialogM` enchaînés deviennent deux étapes d'une feuille (`<ParentFlowModal>`, un seul
 * en-tête, la barre de progression) :
 *  1. **L'offre** — son titre en question (« Nouvelle proposition de groupe », « … pour toi »,
 *     « Nouvelle séance d'essai », « Nouvelle proposition de remplacement »), ce qu'elle dit
 *     (`<TutorOfferDetails>`), et deux actions : « Accepter la proposition », « Masquer la
 *     proposition ». « Retour » était une sortie : c'est la croix.
 *  2. **La confirmation** — la question du legacy, selon l'offre (un essai, un nouvel élève ajouté à
 *     des séances existantes et/ou à une séance à créer, un groupe, un remplacement), l'offre
 *     redite, et pour un groupe l'engagement : « En candidatant, tu confirmes être prêt.e à assurer
 *     ces séances. Tu recevras une confirmation si c'est validé pour toi. » Une action — « Je
 *     candidate » ou « Je confirme que j'accepte » — et « Annuler ».
 *  Depuis la page d'un lien, la confirmation est seule (`entry="page"`) : l'offre est déjà à l'écran.
 *  - **Retirer sa candidature** — une offre candidatée : « Retirer ta candidature ? », une étape.
 *
 * Un **changement de créneau** (`offer.slotChange`, legacy `handleSlotChange`) n'a qu'une étape à
 * l'ouverture, `detail` et `confirm` confondus : la question (« Garder Léa en maths ? »), quel cours
 * change, les horaires en cartes d'option (un bouton radio quand un seul est à prendre, des coches
 * sinon ; un horaire déjà pris par un autre tuteur se montre, éteint), ce qu'il faut savoir, et deux
 * actions — « Accepter la proposition » (« Accepter les 2 créneaux »), ouverte seulement avec autant
 * d'horaires que de cours qui changent, et « Je ne peux pas ». Celle-ci mène à `decline` : « Tu ne
 * peux pas ? » et ce qu'elle déclenche, dit par le serveur, avant que rien ne parte.
 *
 * Champion : la Zoé du premier `DialogM` n'est pas reprise — un champion n'apparaît que là où il agit,
 * jamais dans une boîte de dialogue (le livre V16).
 *
 * Contrôlé : l'étape, l'offre et l'envoi en cours arrivent en props.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { TutorOfferDetails } from "../../organisms/TutorOfferDetails"
import { ParentFlowModal } from "../ParentFlowModal"
import {
    type UITutorOffer,
    type UITutorOfferFlowStep,
    type UITutorSlotChange,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

const TEXTS = {
    acceptSlots: (pick: number) => (pick > 1 ? `Accepter les ${pick} créneaux` : TEXTS.accept),
    decline: "Je ne peux pas",
    declineTitle: "Tu ne peux pas ?",
    taken: "Déjà pris par un autre tuteur",
    name: "Répondre à une offre de cours",
    accept: "Accepter la proposition",
    hide: "Masquer la proposition",
    apply: "Je candidate",
    acceptSubstitution: "Je confirme que j'accepte",
    cancel: "Annuler",
    back: "Retour",
    commitment: "En candidatant, tu confirmes être prêt.e à assurer ces séances.",
    confirmation: "Tu recevras une confirmation si c'est validé pour toi.",
    withdrawTitle: "Retirer ta candidature ?",
    withdrawBody: "Tu ne seras plus candidat.e pour cette proposition.",
    withdraw: "Retirer ma candidature",
}

/** Le titre de l'offre ouverte (`getTitle`) ; celui d'un changement de créneau est sa question (`dialogTitle`). */
export function tutorOfferTitle(offer: UITutorOffer): string {
    if (offer.slotChange != null) return offer.slotChange.question
    if (offer.kind === "substitution") return "Nouvelle proposition de remplacement"
    if (offer.kind === "trial") return "Nouvelle séance d'essai"
    return offer.adhoc === true ? "Nouvelle proposition pour toi" : "Nouvelle proposition de groupe"
}

/**
 * La question de la confirmation (`confirmationText`), et sa suite quand elle en a une : « Es-tu
 * sûr.e de candidater ? Tu créeras un nouveau live et accueilleras l'élève dans tes séances
 * existantes. » se lit en titre et en première ligne, pas en un titre de trois lignes.
 */
function confirmQuestion(offer: UITutorOffer): { title: string; lead?: string } {
    if (offer.kind === "substitution") return { title: "Es-tu sûr.e d'accepter ce remplacement ?" }
    if (offer.kind === "trial") return { title: "Es-tu sûr.e de candidater à cette séance d'essai ?" }
    if (offer.adhoc === true) {
        const hasNew = offer.detail.lines.some((line) => line.kind === "new")
        const hasExisting = offer.detail.lines.some((line) => line.kind === "existing")
        if (hasNew && hasExisting) {
            return {
                title: "Es-tu sûr.e de candidater ?",
                lead: "Tu créeras un nouveau live et accueilleras l'élève dans tes séances existantes.",
            }
        }
        if (hasExisting) return { title: "Es-tu sûr.e d'accueillir cet élève dans tes séances existantes ?" }
    }
    return { title: "Es-tu sûr.e de candidater à cette proposition ?" }
}

/** Le corps du choix : quel cours change, les horaires, ce qu'il faut savoir (`SlotChangeDialogContent`). */
function SlotChangeChoice(props: {
    slotChange: UITutorSlotChange
    selectedSlotIds: number[]
    onToggleSlot?: (liveSessionId: number) => void
}) {
    const { slotChange, selectedSlotIds, onToggleSlot } = props
    return (
        <Stack gap="4">
            <Text size="text-md">{slotChange.intro}</Text>
            <Stack gap="2">
                {slotChange.choices.map((choice) => {
                    const id = choice.liveSessionId
                    return (
                        <OptionCard
                            key={choice.key}
                            label={choice.label}
                            description={choice.note}
                            control={slotChange.pick === 1 ? "radio" : "check"}
                            selected={id != null && selectedSlotIds.includes(id)}
                            disabled={choice.taken || id == null}
                            disabledReason={choice.taken ? TEXTS.taken : undefined}
                            onPress={() => {
                                if (id != null) onToggleSlot?.(id)
                            }}
                        />
                    )
                })}
            </Stack>
            <Text size="text-sm" color="text-muted">
                {slotChange.note}
            </Text>
        </Stack>
    )
}

export type TutorOfferFlowProps = {
    visible: boolean
    onClose: () => void
    offer: UITutorOffer
    step: UITutorOfferFlowStep
    /** `list` (défaut) : l'offre, puis la confirmation. `page` : la confirmation seule. */
    entry?: "list" | "page"
    // Les actions d'une étape sont facultatives : une surface qui n'ouvre pas cette étape ne les passe
    // pas (la page d'un lien n'ouvre que la confirmation), et une action absente n'est pas rendue.
    /** « Accepter la proposition » (l'offre) : passer à la confirmation. */
    onAccept?: () => void
    /** « Masquer la proposition » (l'offre). */
    onHide?: () => void
    /** « Je candidate » / « Je confirme que j'accepte ». */
    onConfirm: () => void
    /** « Retirer ma candidature » (le retrait). */
    onWithdraw?: () => void
    /** Retour de la confirmation à l'offre (`entry: "list"`), ou de « Je ne peux pas » au choix des horaires. */
    onBack?: () => void
    /** Un changement de créneau : les horaires cochés (`live_session_id`). */
    selectedSlotIds?: number[]
    /** Un changement de créneau : le compte d'horaires cochés est celui que le serveur attend (`canAccept`). */
    canAcceptSlots?: boolean
    /** Un changement de créneau : cocher ou décocher un horaire. */
    onToggleSlot?: (liveSessionId: number) => void
    /** Un changement de créneau : « Je ne peux pas », qui ouvre sa confirmation (`decline`). */
    onDecline?: () => void
    /** La réponse qui part. */
    sending?: "accept" | "hide" | "withdraw" | null
    /** Un envoi refusé, dit dans l'étape. */
    errorMessage?: string
    testID?: string
}

export function TutorOfferFlow(props: TutorOfferFlowProps) {
    const {
        visible,
        onClose,
        offer,
        step,
        entry = "list",
        onAccept,
        onHide,
        onConfirm,
        onWithdraw,
        onBack,
        selectedSlotIds = [],
        canAcceptSlots = false,
        onToggleSlot,
        onDecline,
        sending = null,
        errorMessage,
        testID,
    } = props
    const busy = sending != null
    const shell = { visible, onClose, title: TEXTS.name, errorMessage, testID }

    if (step === "decline") {
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={TEXTS.declineTitle}
                step={1}
                totalSteps={1}
                onBack={onBack}
                footer={
                    <Stack gap="2">
                        {onHide != null && (
                            <Button
                                label={TEXTS.decline}
                                color="danger"
                                onPress={onHide}
                                loading={sending === "hide"}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                        <Button
                            label={onBack != null ? TEXTS.back : TEXTS.cancel}
                            variant="outline"
                            onPress={onBack ?? onClose}
                            fullWidth
                        />
                    </Stack>
                }
            >
                <Text size="text-lg">{offer.slotChange?.declineConsequence ?? ""}</Text>
            </ParentFlowModal>
        )
    }

    if (offer.slotChange != null && step !== "withdraw") {
        const slotChange = offer.slotChange
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={slotChange.question}
                step={1}
                totalSteps={1}
                footer={
                    <Stack gap="2">
                        <Button
                            label={TEXTS.acceptSlots(slotChange.pick)}
                            onPress={onConfirm}
                            loading={sending === "accept"}
                            disabled={busy || !canAcceptSlots}
                            fullWidth
                        />
                        {onDecline != null && (
                            <Button
                                label={TEXTS.decline}
                                variant="outline"
                                color="danger"
                                onPress={onDecline}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                    </Stack>
                }
            >
                <SlotChangeChoice
                    slotChange={slotChange}
                    selectedSlotIds={selectedSlotIds}
                    onToggleSlot={busy ? undefined : onToggleSlot}
                />
            </ParentFlowModal>
        )
    }

    if (step === "withdraw") {
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={TEXTS.withdrawTitle}
                step={1}
                totalSteps={1}
                footer={
                    <Stack gap="2">
                        {onWithdraw != null && (
                            <Button
                                label={TEXTS.withdraw}
                                color="danger"
                                onPress={onWithdraw}
                                loading={sending === "withdraw"}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                        <Button label={TEXTS.cancel} variant="outline" onPress={onClose} fullWidth />
                    </Stack>
                }
            >
                <Text size="text-lg">{TEXTS.withdrawBody}</Text>
            </ParentFlowModal>
        )
    }

    if (step === "detail") {
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={tutorOfferTitle(offer)}
                step={1}
                totalSteps={2}
                footer={
                    <Stack gap="2">
                        {onAccept != null && (
                            <Button label={TEXTS.accept} onPress={onAccept} disabled={busy} fullWidth />
                        )}
                        {onHide != null && (
                            <Button
                                label={TEXTS.hide}
                                variant="outline"
                                color="danger"
                                onPress={onHide}
                                loading={sending === "hide"}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                    </Stack>
                }
            >
                <TutorOfferDetails detail={offer.detail} />
            </ParentFlowModal>
        )
    }

    const question = confirmQuestion(offer)
    const fromPage = entry === "page"
    const isGroup = offer.kind !== "substitution"

    return (
        <ParentFlowModal
            {...shell}
            stepTitle={question.title}
            step={fromPage ? 1 : 2}
            totalSteps={fromPage ? 1 : 2}
            onBack={fromPage ? undefined : onBack}
            footer={
                <Stack gap="2">
                    <Button
                        label={isGroup ? TEXTS.apply : TEXTS.acceptSubstitution}
                        onPress={onConfirm}
                        loading={sending === "accept"}
                        disabled={busy}
                        fullWidth
                    />
                    <Button label={TEXTS.cancel} variant="outline" onPress={onClose} fullWidth />
                </Stack>
            }
        >
            <Stack gap="4">
                {question.lead != null && <Text size="text-lg">{question.lead}</Text>}
                <TutorOfferDetails detail={offer.detail} />
                {isGroup && (
                    <Stack gap="1">
                        <Text size="text-md" weight="semibold">
                            {TEXTS.commitment}
                        </Text>
                        <Text size="text-md" color="text-muted">
                            {TEXTS.confirmation}
                        </Text>
                    </Stack>
                )}
            </Stack>
        </ParentFlowModal>
    )
}
